import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Shield, 
  BarChart3, 
  FileSearch, 
  Upload, 
  Lock,
  ChevronRight,
  CheckCircle2,
  Building2
} from "lucide-react";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#FCD34D] flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#1C1917]" />
              </div>
              <span className="font-semibold text-lg text-white">BankFlow Control</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-zinc-400 hover:text-white hover:bg-zinc-800" data-testid="header-login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24] font-medium" data-testid="header-register-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FCD34D]/10 border border-[#FCD34D]/20 text-[#FCD34D] text-sm mb-6">
                <Shield className="w-4 h-4" />
                Enterprise-grade security
              </div>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-white mb-6">
                Financial Analysis
                <span className="block text-[#FCD34D]">Made Simple</span>
              </h1>
              <p className="text-lg text-zinc-400 mb-8 leading-relaxed max-w-xl">
                Upload bank statements from multiple accounts, automatically extract transactions with AI-powered OCR, and get consolidated financial insights across your entire business.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24] font-medium h-12 px-8 glow-gold" data-testid="hero-get-started-btn">
                    Start Free Analysis
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-12 px-8" data-testid="hero-signin-btn">
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-slide-up stagger-2">
              <div className="absolute -inset-4 bg-[#FCD34D]/5 rounded-3xl blur-3xl" />
              <div className="relative bg-[#121214] border border-zinc-800 rounded-2xl p-6 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-lg">
                    <span className="text-zinc-400">Total Income</span>
                    <span className="font-mono text-xl text-emerald-500">£247,850.00</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-lg">
                    <span className="text-zinc-400">Direct Expenses</span>
                    <span className="font-mono text-xl text-red-500">-£89,420.00</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-lg">
                    <span className="text-zinc-400">Indirect Expenses</span>
                    <span className="font-mono text-xl text-orange-500">-£42,180.00</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-[#FCD34D]/10 border border-[#FCD34D]/20 rounded-lg">
                    <span className="text-[#FCD34D] font-medium">Net Surplus</span>
                    <span className="font-mono text-xl text-[#FCD34D]">£116,250.00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 md:px-8 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
              Everything You Need
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              A complete financial analysis platform designed for businesses managing multiple bank accounts.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Upload,
                title: "Multi-Format Upload",
                description: "Upload PDFs, CSVs, Excel files, or screenshots. Our AI-powered OCR handles them all."
              },
              {
                icon: FileSearch,
                title: "Smart Extraction",
                description: "Automatically extract transaction data with confidence scores and validation."
              },
              {
                icon: BarChart3,
                title: "Real-Time Analytics",
                description: "Live dashboards with income vs expenses, category breakdowns, and trends."
              },
              {
                icon: Shield,
                title: "Bank-Grade Security",
                description: "Encrypted storage, audit trails, and role-based access control."
              },
              {
                icon: CheckCircle2,
                title: "Review Workflow",
                description: "Review uncertain extractions before finalizing your financial data."
              },
              {
                icon: Lock,
                title: "Audit Trail",
                description: "Every change is logged immutably for compliance and accountability."
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="p-6 bg-[#121214] border border-zinc-800 rounded-xl card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-lg bg-[#FCD34D]/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#FCD34D]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
              How It Works
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Three simple steps to consolidated financial clarity.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Upload Statements", desc: "Drag and drop bank statements from any of your accounts. We support PDFs, images, and spreadsheets." },
              { step: "02", title: "Auto-Classify", desc: "Our AI extracts transactions and classifies them as income, direct costs, or indirect expenses." },
              { step: "03", title: "Analyze & Report", desc: "View consolidated dashboards, generate reports, and make informed financial decisions." }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-[#FCD34D]/10 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute top-8 -right-4 w-8 h-[2px] bg-zinc-800" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-[#121214] border border-zinc-800 rounded-2xl p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#FCD34D]/5 to-transparent" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Take Control?
              </h2>
              <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
                Join businesses using BankFlow Control for consolidated financial analysis across multiple bank accounts.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24] font-medium h-12 px-8 glow-gold-strong" data-testid="cta-get-started-btn">
                  Get Started Free
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-4 h-4 text-[#1C1917]" />
            </div>
            <span className="text-sm text-zinc-400">BankFlow Control</span>
          </div>
          <p className="text-sm text-zinc-500">
            © 2024 BankFlow Control. Secure Financial Analysis Platform.
          </p>
        </div>
      </footer>
    </div>
  );
}
